import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { NewsRow } from "@/components/feature/NewsCard";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 4;

const categories = ["全部", "作品动态", "行业动态", "幕后花絮", "影评"];

const dateOptions = [
  { value: "all", label: "全部时间" },
  { value: "7", label: "近 7 天" },
  { value: "30", label: "近 30 天" },
  { value: "90", label: "近 90 天" },
  { value: "365", label: "近一年" },
];

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "按热门度" },
];

const latestTime = Math.max(
  ...newsList.map((item) => new Date(`${item.date}T00:00:00`).getTime()),
);

function NewsSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="flex animate-pulse items-start gap-4 border-b border-background-200 py-4 last:border-b-0"
        >
          <div className="flex-1 space-y-3">
            <div className="h-3 w-24 rounded bg-background-200"></div>
            <div className="h-4 w-3/4 rounded bg-background-200"></div>
            <div className="h-3 w-1/2 rounded bg-background-100"></div>
          </div>
          <div className="hidden h-[76px] w-[112px] shrink-0 rounded-md bg-background-200 sm:block"></div>
        </div>
      ))}
    </div>
  );
}

export default function NewsListPage() {
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("全部");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  usePageMeta(
    "大桥未久影视资讯 - 最新影视动态",
    "汇总大桥未久相关影视资讯与行业动态，涵盖作品发布、幕后花絮与影评观察，支持按时间与热度筛选。",
    "大桥未久资讯,影视动态,行业新闻,幕后花絮,影评",
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [keyword, category, dateRange, sort]);

  const filtered = useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();

    const result = newsList.filter((item) => {
      if (category !== "全部" && item.category !== category) return false;

      if (dateRange !== "all") {
        const days = Number(dateRange);
        const boundary = latestTime - days * 24 * 60 * 60 * 1000;
        if (new Date(`${item.date}T00:00:00`).getTime() < boundary) return false;
      }

      if (!trimmed) return true;

      const haystack = `${item.title} ${item.summary} ${item.content.join(" ")}`.toLowerCase();
      return haystack.includes(trimmed);
    });

    return result.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return b.date.localeCompare(a.date);
    });
  }, [keyword, category, dateRange, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;
  const isFiltered =
    keyword.trim() !== "" || category !== "全部" || dateRange !== "all" || sort !== "latest";

  const resetFilters = () => {
    setKeyword("");
    setCategory("全部");
    setDateRange("all");
    setSort("latest");
  };

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "资讯" }]} />
          <div className="mt-6 max-w-3xl">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-700">
              News center
            </p>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
              大桥未久影视资讯
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              作品发布、行业观察、幕后记录与影评观点。资讯内容为演示数据，每周一、周四更新。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex w-full min-w-0 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 lg:max-w-md">
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索资讯标题或关键词"
                aria-label="搜索资讯标题或关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>

            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:ml-auto lg:max-w-md">
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-[11px] text-foreground-500">时间范围</span>
                <select
                  value={dateRange}
                  onChange={(event) => setDateRange(event.target.value)}
                  className="min-h-[44px] w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {dateOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-[11px] text-foreground-500">排序</span>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="min-h-[44px] w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {sortOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`min-h-[38px] cursor-pointer whitespace-nowrap rounded-full px-3.5 text-xs transition-colors ${
                  category === item
                    ? "bg-primary-500 font-medium text-background-50"
                    : "border border-background-300 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
                }`}
              >
                {item}
              </button>
            ))}
            {isFiltered && (
              <button
                type="button"
                onClick={resetFilters}
                className="ml-auto inline-flex min-h-[38px] cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs text-foreground-500 transition-colors hover:text-accent-700"
              >
                <i className="ri-refresh-line"></i>
                重置
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
          <p>
            共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span>{" "}
            篇资讯
            {keyword.trim() && <span> · 关键词「{keyword.trim()}」</span>}
          </p>
          <p className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {loading ? "正在加载内容…" : "内容已更新至 2026 年 10 月"}
          </p>
        </div>

        <div className="mt-4 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            {loading ? (
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                <NewsSkeleton />
              </div>
            ) : shown.length > 0 ? (
              <div className="rounded-lg border border-background-200 bg-background-50 px-5">
                {shown.map((item) => (
                  <NewsRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-search-line text-2xl"></i>
                </span>
                <h2 className="mt-4 text-base font-semibold text-foreground-950">
                  没有匹配的资讯
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                  当前筛选条件下暂无内容，试着换一个关键词，或放宽时间范围再看一遍。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  清除筛选条件
                </button>
              </div>
            )}

            {!loading && hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                  className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 text-sm font-medium text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-50"
                >
                  加载更多资讯
                  <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            )}

            {!loading && shown.length > 0 && !hasMore && (
              <p className="mt-8 text-center text-xs text-foreground-400">
                已展示全部 {filtered.length} 篇资讯
              </p>
            )}
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-fire-line text-accent-600"></i>
                热门资讯
              </h2>
              <div className="mt-4 space-y-4">
                {[...newsList]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 5)
                  .map((item, index) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex cursor-pointer gap-3"
                    >
                      <span className="font-label mt-0.5 text-sm font-bold text-accent-500">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm leading-relaxed text-foreground-800 transition-colors group-hover:text-accent-700">
                          {item.title}
                        </p>
                        <p className="mt-1 text-[11px] text-foreground-400">
                          {item.date} · {item.category}
                        </p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-primary-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">浏览作品档案</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                资讯涉及的作品均可在作品档案中查看完整资料与演职员信息。
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Link
                  to="/category/movie"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-primary-200 bg-background-50 px-3.5 text-sm text-primary-700 transition-colors hover:border-accent-400"
                >
                  电影作品
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link
                  to="/category/tv"
                  className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-primary-200 bg-background-50 px-3.5 text-sm text-primary-700 transition-colors hover:border-accent-400"
                >
                  电视剧作品
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}